import { ReactElement, useContext, useEffect } from 'react';
import {
  Container,
  Stack,
  Heading,
  Text,
  TableContainer,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
} from '@chakra-ui/react';
import Head from 'next/head';
import { MarketplaceContext } from 'src/contexts/marketplace';
import { BasicCategory } from 'src/api/v1-api';
import { buildCategoryTree } from 'src/utils/common';

const Terms: React.FC = () => {
  const { marketplace, categories, fetchCategories } = useContext(MarketplaceContext);
  useEffect(() => {
    const script = document.createElement('script');

    script.src = 'https://app.termly.io/embed-policy.min.js';
    script.async = true;

    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  useEffect(() => {
    if (categories.length === 0) {
      fetchCategories();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marketplace?.id, categories]);

  const renderCategoryOptions: (
    categoryTree: BasicCategory[],
    prefix?: string,
  ) => ReactElement[] = (categoryTree, prefix = '') => {
    return categoryTree.flatMap((cat) => {
      if (cat.isVisible === undefined || cat.isVisible) {
        return [
          <Tr key={cat.id}>
            <Th>{prefix + cat.name}</Th>
            <Th isNumeric>{parseFloat(cat.take_rate ?? '0') * 100}%</Th>
          </Tr>,
          ...renderCategoryOptions(cat.children || [], prefix + '\u00A0\u00A0\u00A0\u00A0'),
        ];
      }
      return renderCategoryOptions(cat.children || [], prefix + '\u00A0\u00A0\u00A0\u00A0');
    });
  };

  return (
    <>
      <Head>
        <title>Basic Marketplace Transaction Terms</title>
        <meta name="description" content="Terms" />
      </Head>
      <main>
        <Container>
          <Heading fontSize={{ base: '2xl', md: '3xl' }} fontWeight="semibold" pt={16}>
            Basic Marketplace Transaction Terms
          </Heading>
          <Stack height="full">
            <Stack justifyContent="space-between" height="full">
              <Text fontWeight="medium" noOfLines={2} paddingTop={4}>
                Payment Processing
              </Text>
              <Text fontSize="small">
                Upon engaging in a peer-to-peer transaction on the marketplace operated by Local
                Marketplace Technologies Inc (AKA &quot;Loma&quot;) on behalf of {marketplace?.name}
                , the Purchaser (&quot;Buyer User&quot;) authorizes Loma to act as their limited
                payment collection agent. Buyer User agrees to provide accurate payment details and
                authorizes Loma to charge their chosen payment method for the total purchase price,
                alongside any additional fees or taxes. Loma uses a third-party payment processor to
                handle Buyer Users&apos; payments. Buyer Users make purchases with their preferred
                payment method, and the payment processor handles the transaction. The payment
                processor will facilitate the transfer of the purchase price from the Buyer
                User&apos;s account to Loma.
              </Text>

              <Text fontWeight="medium" noOfLines={2} paddingTop={4}>
                Seller Payouts & Distribution
              </Text>
              <Text fontSize="small">
                Seller (&quot;Seller User&quot;) acknowledges and agrees that Loma maintains the
                right to hold funds upon the peer-to-peer sale of merchandise until confirmation of
                receipt by the purchasing User and/or a third party delivery service. Once a Buyer
                User&apos;s receipt of order is confirmed, and no dispute is raised within 48 hours
                of receipt, the payment, less Loma&apos;s marketplace fees, will be remitted to the
                Seller User. Payments to Seller Users for items sold (i.e. &quot;Payouts&quot;) are
                made by Loma. If you have not received a payout after 5 working days following
                receipt of order, please contact Support. By using Loma, Seller User consents to the
                possibility that payout or distribution of funds may be delayed under certain
                circumstances as determined by Loma. These could involve instances such as dispute
                resolution processes, considerations of fraudulent conduct, violation of Loma
                policies, or other circumstances as provided under Loma&apos;s terms.
              </Text>

              <Text fontWeight="medium" noOfLines={2} paddingTop={4}>
                Marketplace Fees
              </Text>
              <Text fontSize="small">
                Loma charges a fee for most peer-to-peer transactions processed through the
                platform. This fee is automatically deducted from the total payment before the
                balance is remitted to the Seller User. Seller Users and Buyer Users agree that Loma
                shall deduct a commission as stipulated in the Fee Table section of terms and
                conditions for every transaction completed using the Loma platform.
              </Text>

              <Text fontWeight="medium" noOfLines={2} paddingTop={4}>
                Liability, Refunds, & Exchanges
              </Text>
              <Text fontSize="small">
                Loma is not responsible for any issues related to items sold on the marketplace,
                such as counterfeits, misrepresentation, or fraud. It is up to the Seller Users and
                Buyer Users to manage these risks. Unless specified, no refunds or exchanges will be
                provided by Seller Users. In cases where Loma can determine a Seller User&apos;s
                item was delivered significantly different from how it was described, up to $100 of
                a peer-to-peer purchase may be refunded. Contact support for more information about
                this policy. As a Seller User, if Loma determines your item was delivered
                significantly different from how it was described and was not damaged in transit,
                your payout may be forfeit.
              </Text>

              <Text fontWeight="medium" noOfLines={2} paddingTop={4}>
                Indemnity
              </Text>
              <Text fontSize="small">
                User agrees to indemnify and hold harmless Loma, employees, agents, and affiliates
                from any claims, lawsuits, damages, losses, liabilities, and costs (including
                attorneys&apos; fees), arising out of or relating to non-compliance with these
                terms, improper use of the Loma platform, violation of third party rights, or breach
                of any laws, regulations, or user obligations.
              </Text>
              <Text fontWeight="medium" noOfLines={2} paddingTop={4}>
                Compliance with Local Laws
              </Text>
              <Text fontSize="small">
                User&apos;s usage of this platform implies the adherence to laws and regulations
                imposed by their respective geographical jurisdictions. Seller Users and Buyer Users
                are responsible for compliance with all applicable local laws.
              </Text>

              <Text fontSize="small">
                By using Loma, you acknowledge that you have read, understood, and agreed to be
                bound by these terms and conditions. If you disagree with any part of these Terms
                and Conditions, you must discontinue using Loma. Note: These terms are subject to
                changes and modifications at any time and at &quot;Loma&apos;s&quot; sole
                discretion.
              </Text>
              <Text fontWeight="medium" noOfLines={2} paddingTop={4}>
                Fee Table
              </Text>
              <Text fontSize="small">
                As of August 27th, 2024, Loma&apos;s marketplace fee is 10% of the purchase price of
                any transaction between a Buyer User and a Seller User where Loma acts as a limited
                payment collection agent. Transactions between Loma and Seller Users for other
                promotional products and services are subject to their own specified fees.
              </Text>
              <Text fontWeight="medium" noOfLines={2} paddingTop={4}>
                Fee Schedule
              </Text>
              <TableContainer>
                <Table variant={'simple'} size={'sm'}>
                  <Thead>
                    <Tr>
                      <Th>Category</Th>
                      <Th isNumeric>Fee</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {renderCategoryOptions(
                      buildCategoryTree(
                        categories.filter(
                          (cat) => cat.type === 'marketplace' && cat.name.toLowerCase() !== 'home',
                        ),
                      ),
                    )}
                  </Tbody>
                </Table>
              </TableContainer>
              <Text paddingBottom={4}></Text>
            </Stack>
          </Stack>
        </Container>
      </main>
    </>
  );
};

export default Terms;
